import * as v0 from "@aidkitorg/types/lib/survey";
import { useContext } from "react";
import InterfaceContext from "../../Context";
import { useToast } from "@aidkitorg/component-library";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import { usePost } from "../../API";
import { useUserAllowed } from "../PermissionsUtil";

export const CustomAddButton = (button: v0.CustomAddApplicantButton) => {

  const { lang } = useContext(InterfaceContext);
  const { buttonText, redirectToSubsurvey } = button;

  const { toast } = useToast();

  const searchForDuplicates = usePost('/applicant/search_for_duplicates');
  const addNewApplicant = usePost('/applicant/create');
  const isAllowed = useUserAllowed(button.limitedToTags || []);

  if (!isAllowed) return null;

  async function createCustomApplicant () {  
    const info: { legal_name: string, [key: string]: string } = { legal_name: '' };
  
    const presetFields = Array.isArray(button.presetFields) ? button.presetFields : [];
    const subsurveyRef = Array.isArray(button.presetFields) ? undefined : button.presetFields;

    if (subsurveyRef) {
      // Redirect to the special 'create applicant' subsurvey
      window.open(`/i/${subsurveyRef.path}/${button.buttonId}`, '_blank')
      return;
    }
    
    const prompts = presetFields.filter((item: v0.SetField) => item.source.kind === 'Prompt') as (v0.SetField & { source: v0.Prompt })[];
    for (const item of prompts) {
      const value = await prompt(item.source.prompt[lang]);
      if (!value) return;
      info[item.targetField] = value;
    }
    
    // Make sure we have a legal name
    if (!info.legal_name) {
      info.legal_name = buttonText?.[lang] || 'Applicant Name';
    }
  
    const autoSetFields = presetFields.filter((item) => item.source.kind === 'StringValue') as (v0.SetField & { source: v0.StringValue })[];
    for (const item of autoSetFields) {
      info[item.targetField] = item.source.value;
    }

    const dupesResult = await searchForDuplicates({ 
      buttonId: button.buttonId, 
      info 
    });
    if (dupesResult.status === 'error') {
      toast({
        description: dupesResult.error,
        variant: 'error',
        duration: 1000,
      });
      return;
    }

    // Show all warnings 
    const warnings = dupesResult.warnings.map((warning) => {
      return { content: <div key={'warning-' + lang}>{warning.warning[lang]}</div>, 
        string: (warning.warning[lang] || warning.warning['en']).replace('$link', `<a href="/a/${warning.applicants[0].uid}">${warning.applicants[0].name}</a>`) };
    });

    // Filter warnings to only include warnings that prevent adding a new applicant
    const cannotAdd = dupesResult.warnings.filter((warning) => warning.cannotAdd);

    if (cannotAdd.length) {
      window.alert(
        `Found some existing applicants. Unfortunately one or more mean that you cannot add this applicant. 
        \nDetails:\n\n ${warnings.map(w => w.string).join('\n')}`
      );
      return;
    }

    // If we get here, we can add the applicant, but we might still have warnings
    const addApplicant = warnings.length > 0 
      ? window.confirm(
        warnings.map(w => w.string).join('\n') +
          "\n\nWe encountered some duplicates. If you'd like to cancel, click cancel. Otherwise, click confirm.") 
      : true;
    if (addApplicant) {
      const result = await addNewApplicant(info);
      if (result.success) {
        window.location.href = `/a/${result.newId}/${redirectToSubsurvey}`;
      } 
    }
    
  }

  return (
    <div className={'my-3 border-gray-200 text-gray-600 hover:text-indigo-600'} key={button.buttonId}>
      <a
        tabIndex={0}
        className='flex items-center ml-1 pl-2 text-gray-600 hover:text-indigo-600 font-medium cursor-pointer'
        onClick={() => createCustomApplicant()}>
        <PlusCircleIcon className="text-gray-600 hover:text-indigo-600 mr-1 flex-shrink-0" width={20} />
        <div>{button.buttonText?.[lang] || 'Add Applicant'}</div>
      </a>
    </div>
  )
}